import clsx from 'clsx';
import { Angry, ArrowRightLeft, AtSign, Bell, CloudAlert, Inbox, LifeBuoy, MessageCircle, Phone, Smartphone, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearNotificationLog,
  markAllRead,
  markAttendanceRead,
  NotificationEntry,
  NotificationKind,
  useNotificationLog,
} from '../app/notification-log';
import { since } from '../lib/format';

const ICON: Record<NotificationKind, typeof Bell> = {
  queue: Inbox,
  message: MessageCircle,
  transfer: ArrowRightLeft,
  mention: AtSign,
  sync: CloudAlert,
  channel: Smartphone,
  call: Phone,
  help: LifeBuoy,
  mood: Angry,
};

const BASE_TITLE = typeof document !== 'undefined' ? document.title : '';

/**
 * Sino com o histórico dos avisos (de onde veio o bipe): fila, mensagens, transferências,
 * menções, pendências do Agidesk e WhatsApp desconectado. Clicar abre o atendimento.
 */
export function NotificationCenter({ onOpenAttendance }: { onOpenAttendance: (id: string) => void }) {
  const entries = useNotificationLog();
  const [open, setOpen] = useState(false);
  /** Ids que estavam sem ler quando o painel abriu (continuam destacados enquanto ele está aberto). */
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const box = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const unread = entries.filter((e) => !e.read).length;
  const [, tick] = useState(0);

  // Atendimento aberto na tela: os avisos dele já foram vistos.
  const openId = /^\/atendimento\/([0-9a-f-]{36})/.exec(location.pathname)?.[1] ?? null;
  useEffect(() => {
    if (openId) markAttendanceRead(openId);
  }, [openId, entries.length]);

  // Contador no título da aba (dá para ver de outra janela).
  useEffect(() => {
    document.title = unread > 0 ? `(${unread}) ${BASE_TITLE}` : BASE_TITLE;
  }, [unread]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('mousedown', onDown);
      clearInterval(t);
    };
  }, [open]);

  const toggle = () => {
    if (!open) {
      setFresh(new Set(entries.filter((e) => !e.read).map((e) => e.id)));
      markAllRead();
    }
    setOpen((o) => !o);
  };

  const choose = (e: NotificationEntry) => {
    if (!e.attendanceId) return;
    setOpen(false);
    onOpenAttendance(e.attendanceId);
  };

  return (
    <div className="relative" ref={box}>
      <button className="relative rounded p-1.5 text-fg-soft hover:bg-muted" onClick={toggle} title="Avisos recentes">
        <Bell className="size-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] leading-none font-semibold text-accent-fg">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-x-2 top-14 z-30 rounded-md md:absolute md:inset-x-auto md:top-auto md:right-0 md:mt-1 md:w-96 border border-border bg-panel text-sm shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="font-semibold">Avisos</span>
            {entries.length > 0 && (
              <button className="flex items-center gap-1 text-xs text-fg-soft hover:text-fg" onClick={clearNotificationLog} title="Apagar o histórico deste navegador">
                <Trash2 className="size-3.5" /> Limpar
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {entries.length === 0 && <p className="px-3 py-6 text-center text-xs text-fg-soft">Nenhum aviso ainda.</p>}
            {entries.map((e) => {
              const Icon = ICON[e.kind] ?? Bell;
              return (
                <button
                  key={e.id}
                  onClick={() => choose(e)}
                  disabled={!e.attendanceId}
                  className={clsx(
                    'flex w-full gap-2.5 border-b border-border px-3 py-2 text-left last:border-0',
                    e.attendanceId ? 'hover:bg-muted' : 'cursor-default',
                    fresh.has(e.id) && 'bg-accent/5',
                  )}
                >
                  <Icon className={clsx('mt-0.5 size-4 shrink-0', e.kind === 'sync' || e.kind === 'channel' ? 'text-danger' : 'text-fg-soft')} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className={clsx('truncate', fresh.has(e.id) && 'font-semibold')}>
                        {e.title}
                        {e.count > 1 && <span className="ml-1 text-xs font-normal text-fg-soft">({e.count})</span>}
                      </span>
                      <span className="shrink-0 text-[11px] text-fg-soft">{since(e.at)}</span>
                    </span>
                    {e.body && <span className="block truncate text-xs text-fg-soft">{e.body}</span>}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="border-t border-border px-3 py-1.5 text-[11px] text-fg-soft">Últimos 7 dias, neste navegador.</p>
        </div>
      )}
    </div>
  );
}
